import CONTENT_TYPES from './content_types.json'

export { CONTENT_TYPES }

export const CONTENT_TYPE_MAP = Object.fromEntries(
  CONTENT_TYPES.map((type) => [type.key, type])
)

export const TEXT_CONTENT_TYPES = CONTENT_TYPES.filter(
  (type) => type.outputFormat === 'text'
)

export const LEGACY_PLATFORM_TO_CONTENT_TYPE = {
  linkedin: 'linkedin-post',
  twitter: 'x-post',
  'linkedin-article': 'linkedin-article',
  newsletter: 'newsletter-issue',
  'linkedin-carousel': 'linkedin-carousel',
}

export const STUDIO_SNAPSHOT_VERSION = 2
export const LEGACY_STUDIO_SNAPSHOT_VERSION = 1

export function getContentType(key) {
  return CONTENT_TYPE_MAP[key] || CONTENT_TYPE_MAP['linkedin-post']
}

export function inferContentTypeKey(value) {
  if (CONTENT_TYPE_MAP[value]) return value
  return LEGACY_PLATFORM_TO_CONTENT_TYPE[value] || 'linkedin-post'
}

export function isKnownContentTypeKey(value) {
  return !!CONTENT_TYPE_MAP[value]
}

export function isRecognizedContentTypeInput(value) {
  if (!value) return false
  return (
    isKnownContentTypeKey(value) || !!LEGACY_PLATFORM_TO_CONTENT_TYPE[value]
  )
}

export function toLegacyPlatformKey(contentTypeKey) {
  const type = getContentType(contentTypeKey)
  // Returns 'x' (not the historical 'twitter') because every other surface
  // in the studio uses 'x' as the platform_family for X content. Mirror of
  // contentTypeUtils.cjs — both must stay aligned. See
  // docs/comprehensive-audit-2026-05-08.md Bug 1+2.
  if (type.platform === 'x') return 'x'
  if (type.platform === 'newsletter') return 'newsletter'
  if (type.platform === 'linkedin' && type.subtype === 'article')
    return 'linkedin-article'
  if (type.platform === 'linkedin' && type.subtype === 'carousel')
    return 'linkedin-carousel'
  // Instagram (I1, 2026-07-04). Without this, instagram-post fell into the
  // legacy 'linkedin' catch-all below and the cadence + credential gates
  // evaluated the WRONG platform's policy (first live IG publish was
  // cancelled by LinkedIn's already-consumed daily cap) — the same
  // wrong-key gate-mismatch class as the 2026-05-08 'twitter'-vs-'x' bug
  // this function's comment already warns about.
  if (type.platform === 'instagram') return 'instagram'
  return 'linkedin'
}

export function normalizePost(post) {
  const contentType = inferContentTypeKey(post.contentType || post.platform)
  return {
    ...post,
    contentType,
    platform: toLegacyPlatformKey(contentType),
  }
}

export function normalizeIdea(idea) {
  return {
    ...idea,
    contentType: inferContentTypeKey(idea.contentType),
    metadata:
      idea.metadata && typeof idea.metadata === 'object' ? idea.metadata : {},
  }
}

export function countWords(text) {
  const words = (text || '').trim().match(/\S+/g)
  return words ? words.length : 0
}

export function splitThreadSegments(text) {
  const segments = (text || '')
    .split(/\n(?=\d+\/\s)/)
    .map((segment) => segment.trim())
    .filter(Boolean)

  return segments.length ? segments : [(text || '').trim()]
}

export function stripThreadPrefix(segment) {
  return segment.replace(/^\d+\/\s*/, '').trim()
}

export function getLengthMetrics(contentTypeKey, text) {
  const type = getContentType(contentTypeKey)
  const safeText = text || ''

  if (type.lengthUnit === 'words') {
    const current = countWords(safeText)
    const isUnder = type.minLength != null && current < type.minLength
    const isOver = type.maxLength != null && current > type.maxLength

    return {
      type,
      current,
      min: type.minLength,
      max: type.maxLength,
      unit: 'words',
      isUnder,
      isOver,
      isValid: !isUnder && !isOver,
      segments: [],
    }
  }

  if (type.lengthUnit === 'chars') {
    if (type.supportsThread) {
      const rawSegments = splitThreadSegments(safeText)
      const segments = rawSegments.map((segment, index) => {
        const current = stripThreadPrefix(segment).length

        return {
          index: index + 1,
          current,
          max: type.maxLength,
          isOver: current > type.maxLength,
        }
      })
      const isOver = segments.some((segment) => segment.isOver)

      return {
        type,
        current: segments[0]?.current || 0,
        min: type.minLength,
        max: type.maxLength,
        unit: 'chars',
        isUnder: false,
        isOver,
        isValid: !isOver,
        segments,
      }
    }

    const current = safeText.length
    const isUnder = type.minLength != null && current < type.minLength
    const isOver = type.maxLength != null && current > type.maxLength

    return {
      type,
      current,
      min: type.minLength,
      max: type.maxLength,
      unit: 'chars',
      isUnder,
      isOver,
      isValid: !isUnder && !isOver,
      segments: [],
    }
  }

  return {
    type,
    current: 0,
    min: type.minLength,
    max: type.maxLength,
    unit: '',
    isUnder: false,
    isOver: false,
    isValid: true,
    segments: [],
  }
}

export function createStudioSnapshot({
  posts = [],
  archivedPosts = [],
  ideas = [],
  archivedIdeas = [],
  startDate = '',
}) {
  return {
    version: STUDIO_SNAPSHOT_VERSION,
    exportedAt: new Date().toISOString(),
    posts: posts.map(normalizePost),
    archivedPosts: archivedPosts.map(normalizePost),
    ideas: ideas.map(normalizeIdea),
    archivedIdeas: archivedIdeas.map(normalizeIdea),
    startDate: typeof startDate === 'string' ? startDate : '',
  }
}

export function normalizeStudioSnapshot(snapshot) {
  if (!snapshot || typeof snapshot !== 'object' || Array.isArray(snapshot)) {
    throw new Error('Invalid studio backup file')
  }

  if (
    snapshot.version !== STUDIO_SNAPSHOT_VERSION &&
    snapshot.version !== LEGACY_STUDIO_SNAPSHOT_VERSION
  ) {
    throw new Error(
      `Unsupported studio backup version: ${snapshot.version ?? 'unknown'}`
    )
  }

  assertBackupRecordArray(snapshot.posts, 'posts')
  assertBackupRecordArray(snapshot.ideas, 'ideas')
  if (snapshot.version === STUDIO_SNAPSHOT_VERSION) {
    assertBackupRecordArray(snapshot.archivedPosts, 'archivedPosts')
    assertBackupRecordArray(snapshot.archivedIdeas, 'archivedIdeas')
  }

  if (snapshot.startDate != null && typeof snapshot.startDate !== 'string') {
    throw new Error('Studio backup startDate must be a string')
  }

  return {
    version: STUDIO_SNAPSHOT_VERSION,
    exportedAt:
      typeof snapshot.exportedAt === 'string' ? snapshot.exportedAt : '',
    posts: snapshot.posts.map(normalizePost),
    archivedPosts: Array.isArray(snapshot.archivedPosts)
      ? snapshot.archivedPosts.map(normalizePost)
      : [],
    ideas: snapshot.ideas.map(normalizeIdea),
    archivedIdeas: Array.isArray(snapshot.archivedIdeas)
      ? snapshot.archivedIdeas.map(normalizeIdea)
      : [],
    startDate: snapshot.startDate || '',
  }
}

function assertBackupRecordArray(records, label) {
  if (!Array.isArray(records)) {
    throw new Error(`Studio backup must include a ${label} array`)
  }
  if (records.some((record) => !record || typeof record !== 'object')) {
    throw new Error(`Studio backup ${label} must contain objects`)
  }
}

export default {
  CONTENT_TYPES,
  CONTENT_TYPE_MAP,
  TEXT_CONTENT_TYPES,
  LEGACY_PLATFORM_TO_CONTENT_TYPE,
  LEGACY_STUDIO_SNAPSHOT_VERSION,
  STUDIO_SNAPSHOT_VERSION,
  getContentType,
  inferContentTypeKey,
  isKnownContentTypeKey,
  isRecognizedContentTypeInput,
  toLegacyPlatformKey,
  normalizePost,
  normalizeIdea,
  countWords,
  splitThreadSegments,
  stripThreadPrefix,
  getLengthMetrics,
  createStudioSnapshot,
  normalizeStudioSnapshot,
}
